import { MODEL_CATALOG } from '../models/catalog';
import {
  ENVIRONMENT_CATALOG,
  getEnvironmentDefinition,
  type EnvironmentDefinition,
} from './environmentCatalog';
import {
  ENVIRONMENT_VIEWS,
  MODEL_VIEWS,
  PreviewRenderer,
} from './PreviewRenderer';
import {
  createEnvironmentPreviewScene,
  createModelPreviewScene,
  disposePreviewScene,
} from './createPreviewScene';
import type { GalleryRoute } from './routes';

interface CatalogCard {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly eyebrow: string;
}

interface CatalogSection {
  readonly id: string;
  readonly label: string;
  readonly cards: readonly CatalogCard[];
}

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const result = document.createElement(name);
  if (className) result.className = className;
  if (text) result.textContent = text;
  return result;
}

function galleryNav(active?: 'models' | 'environments'): HTMLElement {
  const nav = element('nav', 'gallery-nav');
  nav.setAttribute('aria-label', 'Gallery');
  const home = element('a', 'gallery-wordmark', 'Soft Run');
  home.href = '/';
  const links = element('div', 'gallery-nav-links');
  const models = element('a', active === 'models' ? 'is-active' : undefined, 'Models');
  models.href = '/models';
  const environments = element('a', active === 'environments' ? 'is-active' : undefined, 'Environments');
  environments.href = '/environments';
  links.append(models, environments);
  nav.append(home, links);
  return nav;
}

function indexPage(
  mount: HTMLElement,
  active: 'models' | 'environments',
  title: string,
  introduction: string,
  sections: readonly CatalogSection[],
): void {
  const page = element('div', 'gallery-shell gallery-index');
  const header = element('header', 'gallery-index-header');
  const eyebrow = element('p', 'gallery-eyebrow', 'Field guide');
  header.append(eyebrow, element('h1', undefined, title), element('p', 'gallery-lede', introduction));
  const guide = element('nav', 'catalog-guide');
  guide.setAttribute('aria-label', `${title} sections`);
  sections.forEach((section) => {
    const link = element('a', undefined, section.label);
    link.href = `#${section.id}`;
    guide.append(link);
  });

  const catalog = element('div', 'catalog-sections');
  let cardNumber = 0;
  for (const section of sections) {
    const sectionElement = element('section', 'catalog-section');
    sectionElement.id = section.id;
    const heading = element('header', 'catalog-section-header');
    heading.append(
      element('h2', undefined, section.label),
      element('span', undefined, `${section.cards.length} ${section.cards.length === 1 ? 'study' : 'studies'}`),
    );
    const list = element('ul', 'catalog-list');
    for (const card of section.cards) {
      cardNumber += 1;
      const item = element('li', 'catalog-item');
      const link = element('a', 'catalog-link');
      link.href = `/${active}/${encodeURIComponent(card.id)}`;
      link.append(
        element('span', 'catalog-number', String(cardNumber).padStart(2, '0')),
        element('span', 'catalog-eyebrow', card.eyebrow),
        element('strong', 'catalog-name', card.name),
        element('span', 'catalog-description', card.description),
        element('span', 'catalog-arrow', 'View study →'),
      );
      item.append(link);
      list.append(item);
    }
    sectionElement.append(heading, list);
    catalog.append(sectionElement);
  }
  page.append(galleryNav(active), header);
  if (sections.length > 1) page.append(guide);
  page.append(catalog);
  mount.append(page);
}

function detailHeader(
  active: 'models' | 'environments',
  name: string,
  description: string,
  eyebrow: string,
): HTMLElement {
  const header = element('header', 'gallery-detail-header');
  const back = element('a', 'gallery-back', `← All ${active}`);
  back.href = `/${active}`;
  const copy = element('div', 'gallery-detail-copy');
  copy.append(
    element('p', 'gallery-eyebrow', eyebrow),
    element('h1', undefined, name),
    element('p', 'gallery-detail-description', description),
  );
  header.append(back, copy);
  return header;
}

function modelDetail(mount: HTMLElement, id: string, captureMode: boolean): () => void {
  const definition = MODEL_CATALOG.find((model) => model.id === id);
  const preview = createModelPreviewScene(id);
  if (!definition || !preview) return notFound(mount, `No model named “${id}” lives here.`);

  document.title = `${definition.name} model · Soft Run`;
  const page = element('div', 'gallery-shell gallery-detail');
  const stage = element('div', 'preview-stage model-preview-stage');
  page.append(
    galleryNav('models'),
    detailHeader('models', definition.name, definition.tagline, `${definition.biome} model`),
    stage,
  );
  mount.append(page);
  const renderer = new PreviewRenderer(stage, preview.scene, {
    views: MODEL_VIEWS,
    bounds: preview.bounds,
    captureMode,
  });
  return () => {
    renderer.dispose();
    disposePreviewScene(preview.scene);
  };
}

function environmentDetail(mount: HTMLElement, id: string, captureMode: boolean): () => void {
  const definition = getEnvironmentDefinition(id);
  if (!definition) return notFound(mount, `No environment named “${id}” lives here.`);

  document.title = `${definition.name} environment · Soft Run`;
  const preview = createEnvironmentPreviewScene(definition);
  const page = element('div', 'gallery-shell gallery-detail environment-detail');
  const stage = element('div', 'preview-stage environment-preview-stage');
  page.append(
    galleryNav('environments'),
    detailHeader('environments', definition.name, definition.tagline, 'Mountain environment'),
    stage,
  );
  mount.append(page);
  const renderer = new PreviewRenderer(stage, preview.scene, {
    views: ENVIRONMENT_VIEWS,
    bounds: preview.bounds,
    background: definition.sky,
    captureMode,
  });
  return () => {
    renderer.dispose();
    disposePreviewScene(preview.scene);
  };
}

function notFound(mount: HTMLElement, message = 'That trail does not exist.'): () => void {
  document.title = 'Not found · Soft Run';
  const page = element('div', 'gallery-shell gallery-empty');
  const copy = element('main', 'gallery-empty-copy');
  copy.append(
    element('p', 'gallery-eyebrow', '404 · Off piste'),
    element('h1', undefined, message),
  );
  const back = element('a', 'gallery-button', 'Return to the mountain');
  back.href = '/';
  copy.append(back);
  page.append(galleryNav(), copy);
  mount.append(page);
  document.documentElement.dataset.galleryReady = 'true';
  return () => undefined;
}

function environmentCard(environment: EnvironmentDefinition): CatalogCard {
  return {
    id: environment.id,
    name: environment.name,
    description: environment.tagline,
    eyebrow: 'Biome study',
  };
}

export function startGallery(mount: HTMLElement, route: GalleryRoute): () => void {
  delete document.documentElement.dataset.galleryReady;
  const captureMode = new URLSearchParams(window.location.search).get('capture') === '1';
  document.documentElement.classList.toggle('gallery-capture', captureMode);

  switch (route.kind) {
    case 'models-index':
      document.title = 'Environment models · Soft Run';
      indexPage(
        mount,
        'models',
        'Mountain models',
        'A growing collection of the handmade landmarks, creatures, and oddities scattered through Soft Run.',
        [
          { id: 'lanternwood-models', label: 'Lanternwood', biome: 'lanternwood' },
          { id: 'ice-garden-models', label: 'Blue Ice', biome: 'ice-garden' },
          { id: 'timberline-models', label: 'Timberline', biome: 'timberline' },
        ].map((section) => ({
          id: section.id,
          label: section.label,
          cards: MODEL_CATALOG
            .filter((model) => model.biome === section.biome)
            .map((model) => ({
              id: model.id,
              name: model.name,
              description: model.tagline,
              eyebrow: model.biome,
            })),
        })),
      );
      document.documentElement.dataset.galleryReady = 'true';
      return () => undefined;
    case 'model-detail':
      return modelDetail(mount, route.id, captureMode);
    case 'environments-index':
      document.title = 'Environments · Soft Run';
      indexPage(
        mount,
        'environments',
        'Mountain environments',
        'Three distinct faces of one endless mountain, each with its own weather, terrain, and signs of life.',
        [{
          id: 'mountain-environments',
          label: 'Three faces of the mountain',
          cards: ENVIRONMENT_CATALOG.map(environmentCard),
        }],
      );
      document.documentElement.dataset.galleryReady = 'true';
      return () => undefined;
    case 'environment-detail':
      return environmentDetail(mount, route.id, captureMode);
    case 'not-found':
      return notFound(mount);
  }
}
